// API Types for CENT Admin

// Authentication
export interface AdminLoginRequest {
  email: string;
  password: string;
}

export interface AdminLoginResponse {
  accessToken: string;
  user: {
    id: string;
    email: string;
    role: "admin";
  };
}

// Admin Users (usuarios del panel admin)
export type AdminUserRole = 'admin' | 'super_admin';

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: AdminUserRole;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CreateAdminUserRequest {
  email: string;
  password: string;
  name: string;
  role?: AdminUserRole;
}

export interface UpdateAdminUserRequest {
  name?: string;
  role?: AdminUserRole;
  is_active?: boolean;
  password?: string;
}

// Brands (Marcas)
export interface Brand {
  id: string;
  name: string;
  alias: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateBrandRequest {
  name: string;
  alias: string;
}

export interface UpdateBrandRequest {
  name: string;
  alias: string;
}

// Branches (Sucursales)
export interface Branch {
  id: string;
  brand_id: string;
  name: string;
  alias: string;
  address_street?: string;
  address_number?: string;
  address_colony?: string;
  address_city?: string;
  address_state?: string;
  address_postal_code?: string;
  address_country?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateBranchRequest {
  brand_id: string;
  name: string;
  alias: string;
  address_street?: string;
  address_number?: string;
  address_colony?: string;
  address_city?: string;
  address_state?: string;
  address_postal_code?: string;
  address_country?: string;
}

export interface UpdateBranchRequest {
  name?: string;
  alias?: string;
  address_street?: string;
  address_number?: string;
  address_colony?: string;
  address_city?: string;
  address_state?: string;
  address_postal_code?: string;
  address_country?: string;
}

// POS Users
export type PosUserRole = "cajero" | "admin_caja" | "gerente_sucursal" | "admin_tienda" | "operador_cent";

export interface PosUser {
  id: string;
  branch_id: string;
  full_name: string;
  email: string;
  role: PosUserRole;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CreatePosUserRequest {
  branch_id: string;
  full_name: string;
  email: string;
  password: string;
  role: PosUserRole;
}

export interface UpdatePosUserRequest {
  full_name?: string;
  email?: string;
  password?: string;
  role?: PosUserRole;
  is_active?: boolean;
}

// External Transfers (UI: Pendiente / Completado)
export type ExternalTransferStatus = 'pending' | 'completed';

// Valores aceptados por PUT /admin/transactions/external/:id/status
export type ExternalTransferApiStatus = 'pending' | 'completed' | 'approved';

export interface UpdateExternalTransferStatusRequest {
  status: ExternalTransferApiStatus;
}

export interface ExternalTransfer {
  id: string;
  user_id: string;
  transaction_id?: string | null;
  beneficiary_name: string;
  bank_name: string;
  clabe: string;
  amount: number;
  description?: string | null;
  reference?: string | null;
  status: ExternalTransferStatus;
  /** Estado crudo del API (pending | approved | completed, etc.) */
  rawStatus?: string;
  fee_amount: number;
  tax_amount: number;
  metadata?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
  approved_by?: string | null;
  approved_at?: string | null;
  user?: {
    id: string;
    first_name?: string;
    last_name?: string;
    phone_number?: string;
    email?: string;
  };
  // Datos del emisor (join con app_users)
  emitter_name?: string;
  emitter_phone?: string;
  emitter_email?: string;
}

// MT Center transactions (servicios + TAE)
export type MtCenterTransactionKind = 'service' | 'tae';

export interface MtCenterTransaction {
  id: string;
  userId: string;
  phoneNumber: string;
  userName: string;
  email?: string | null;
  kind: MtCenterTransactionKind;
  amount: number;
  billAmount: number;
  surcharge: number;
  feeAmount: number;
  providerId?: string | null;
  providerName?: string | null;
  accountNumber?: string | null;
  rechargePhone?: string | null;
  reference: string;
  status: string;
  description?: string | null;
  mtCenterTicket?: string | null;
  mtCenterTransaction?: string | null;
  createdAt: string;
}

export interface MtCenterTransactionsSummary {
  count: number;
  totalAmount: number;
  totalSurcharge: number;
}

export interface MtCenterTransactionsFilters {
  userId?: string;
  phone?: string;
  status?: string;
  kind?: MtCenterTransactionKind | 'all';
  startDate?: string;
  endDate?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface MtCenterTransactionsResponse {
  transactions: MtCenterTransaction[];
  total: number;
  limit: number;
  offset: number;
  summary: MtCenterTransactionsSummary;
}

// Customers (Clientes)
export interface Customer {
  id: string;
  name: string;
  lastName: string;
  dateOfBirth: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  email: string;
  phone: string;
  clabe: string | null; // Puede ser null si no está registrada
  created_at?: string;
  updated_at?: string;
  registrationDate?: string;
}

export interface UpdateCustomerRequest {
  name?: string;
  lastName?: string;
  dateOfBirth?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  email?: string;
  phone?: string;
  clabe?: string;
}

// CLABE endpoints
export interface ClabeResponse {
  clabe: string | null;
}

export interface UpdateUserClabeRequest {
  clabe: string; // Debe tener exactamente 18 dígitos
}

export interface SendOtpRequest {
  type?: 'email' | 'phone' | 'both';
}

export interface SendOtpResponse {
  message: string;
  expiresIn: number;
  sentTo: 'email' | 'phone' | 'both';
}

export interface VerifyOtpRequest {
  otp: string;
  purpose: 'update_contact' | 'update_clabe' | 'other';
}

export interface VerifyOtpResponse {
  verified: boolean;
  token: string;
  expiresIn: number;
}

// Customer Balance and Investments
export interface CustomerBalance {
  totalBalance: number;
  availableBalance: number;
  investedBalance: number;
  availableReturns: number;
  totalReturns: number;
  currency: string;
  lastUpdated: string;
}

export interface CustomerInvestment {
  id: string;
  amount: number;
  date: string;
  term: string;
  status: 'active' | 'matured' | 'cancelled' | 'completed';
  returns: number;
  interestRate?: number;
  maturityDate?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CustomerPurchase {
  id: string;
  amount: number;
  date: string;
  description: string;
  type: 'available_balance' | 'returns';
  currency: string;
  created_at?: string;
}

export interface CustomerInfo {
  id: string;
  name: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  clabe: string | null; // Puede ser null si no está registrada
  registrationDate: string;
  created_at?: string;
}

// Insurance Requests (Seguros)
export type InsuranceRequestStatus = 'Pendiente' | 'Aprobado' | 'Rechazado' | 'En Proceso' | 'cancelled';

export interface InsuranceRequest {
  id: string;
  fechaContratacion: string;
  telefono: string;
  email: string;
  nombreCompleto: string; // Nombre completo del contratante
  fechaNacimiento: string;
  rfc: string | null; // Puede ser null si no está registrado
  curp: string | null; // Puede ser null si no está registrado
  sexo: 'M' | 'F' | null; // Puede ser null si no está registrado
  status: InsuranceRequestStatus;
  // Detalles del beneficiario
  beneficiario: string | null; // Nombre del beneficiario o "N/A"
  beneficiarioTelefono?: string | null;
  beneficiarioEmail?: string | null;
  // Detalles del seguro
  tipoSeguro?: string | null; // Tipo de seguro (coverage_type/plan_type)
  plazo?: string | null; // Plazo del seguro (payment_frequency)
  monto?: number | null; // Monto (coverage_amount || monthly_premium || annual_premium)
  numeroPoliza?: string | null; // Número de póliza
  // Campos adicionales (pueden no estar presentes)
  notes?: string;
  approvedBy?: string | null;
  approvedAt?: string | null;
  rejectedReason?: string | null;
  created_at?: string;
  updated_at?: string;
  // Campos legacy para compatibilidad (si el backend los envía separados)
  nombre?: string;
  apellidoPaterno?: string;
  apellidoMaterno?: string | null;
}

export interface InsuranceRequestsResponse {
  data: InsuranceRequest[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UpdateInsuranceStatusRequest {
  status: InsuranceRequestStatus;
  notes?: string;
  rejectedReason?: string;
  individualPolicyNumber?: string;
}

// Investment Rates (Tasas de Interés)
export interface InvestmentRate {
  type: 'PRO' | 'Normal';
  interest_rate: number;
  updated_at: string;
}

export interface UpdateInvestmentRateRequest {
  type: 'pro' | 'normal';
  interestRate: number;
}

// API Error Response
export interface ApiErrorResponse {
  statusCode: number;
  message: string;
  error: string;
}

// API Error class for better error handling
export class ApiError extends Error {
  public statusCode: number;
  public error: string;

  constructor(message: string, statusCode: number, error: string) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.error = error;
  }
}
